import React, { ReactElement, useEffect, useState } from 'react';
import styled from 'styled-components';
import _ from 'lodash';
import { FilterSideBar, Search } from '@arcflight/tf-component-library';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import InnerMenuLayoutNew from '../../layouts/InnerMenuLayoutNew';
import { AircraftAuthenticationWrapper } from '../../components/_utils/AuthenticationWrapper';
import { AircraftPermission, AircraftResource, CustomField } from '../../models/aircraft';
import { changeDrawerContent, changeDrawerMode, changeDrawerVisibility } from '../../models/drawer';
import useIsFeatureFlagActive from '../../utils/isFeatureFlagActive';
import { getSingleAircraft } from '../../models/aircraft/actions';
import TFDropdownMenu from '../../components/TFDropdownMenu/TFDropdownMenu';
import { DashboardState } from '../../models';
import { usePrevious } from '../../utils/utils';
import useGetOperatorSetting from '../../utils/useGetOperatorSetting';
import plusIcon from '../../assets/plus.svg';
import AIIcon from '../../assets/icon-ai.svg';
import TFLoading from '../../components/TFLoading';
import customFieldFilterModel from './customFieldFilterModel';
import CustomFieldsTable from './CustomFieldTable';
import CustomFieldDrawer from './CustomFieldDrawer';

const Wrapper = styled.div`
  border-radius: 2px;
  box-shadow: ${({ theme }): string => `0 0 16px 0 ${theme.colours.whiteShadow}`};
  border: ${({ theme }): string => `solid 2px ${theme.colours.white}`};
  background-color: ${({ theme }): string => theme.colors.zircon};
  padding: 28px;
`;

const GridWrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr 220px;
  grid-gap: 16px;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;
`;

const Count = styled.div`
  margin-left: 8px;
`;

const TableColumn = styled.div``;

const FilterWrapper = styled.div``;

const TableHeader = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 20px;
`;

const ActionWrapper = styled.div`
  display: flex;
`;

const SearchWrapper = styled.div`
  margin-right: 12px;
  div,
  img,
  button,
  input {
    box-sizing: revert;
    line-height: normal;
  }
`;

const AddButtonText = styled.div`
  color: #fff;
  font-weight: 500;
  align-items: center;
  display: flex;
  img {
    margin-right: 8px;
    margin-top: 2px;
  }
`;

const BlackText = styled.span`
  color: ${({ theme }): string => theme.colours.graphite};
`;

const UseAIWrapper = styled.span`
  color: ${({ theme }): string => theme.colours.purple};
  display: flex;
  img {
    width: 14px;
  }
`;

const CustomFields: React.FC = () => {
  const [customFieldsData, setCustomFieldsData] = useState(null);
  const [pageSize, setPageSize] = useState(100);
  const [pageIndex, setPageIndex] = useState(1);
  const [searchValue, setSearchValue] = useState('');
  const [filters, setFilters] = useState(null);
  const [reset, setReset] = useState(false);
  const [loading, setLoading] = useState(false);
  const [sortBy, setSortBy] = useState([]);

  const {
    aircraft: { aircraftMap },
  } = useSelector((state: DashboardState) => state);

  const { id } = useParams<{ id: string }>();
  const dispatch = useDispatch();
  const { formatMessage } = useIntl();

  const foundAircraftCustomFields = aircraftMap.get(id)?.custom_fields;
  const previousAircraftCustomFields = usePrevious(foundAircraftCustomFields);
  const mxCheckRename = useGetOperatorSetting('mx_check_name', id);
  const chatBotFlagEnabled = useIsFeatureFlagActive(
    'custom-field-chatbot',
    'operator',
    aircraftMap.get(id)?.operator_id,
  );

  const handleAddCustomField = (useAi = false): void => {
    dispatch(changeDrawerVisibility({ payload: true }));
    dispatch(changeDrawerContent({ payload: { content: <CustomFieldDrawer useAi={useAi} /> } }));
    dispatch(changeDrawerMode({ payload: 'add' }));
  };

  const addOptions = ():
    { title: ReactElement; value: string; onClick: () => void; id: string; aiStyle: boolean }[] => {
    const options = [
      {
        title: <BlackText>Default method</BlackText>,
        value: '',
        onClick: handleAddCustomField,
        id: 'defaultMethod',
        aiStyle: false,
      },
    ];
    if (chatBotFlagEnabled) {
      options.push({
        title: (
          <UseAIWrapper>
            Using AI chatbot
            <img src={AIIcon} alt="AI icon" />
          </UseAIWrapper>
        ),
        value: '',
        onClick: (): void => handleAddCustomField(true),
        id: 'aiMethod',
        aiStyle: true,
      });
    }
    return options;
  };

  useEffect(() => {
    if (id) {
      setLoading(true);
      dispatch(getSingleAircraft({ payload: id }));
    }
  }, [dispatch, id]);

  useEffect(() => {
    const checkIfFiltersActive = (): boolean => {
      let active = false;
      if (filters && Object.values(filters).some((item: string[]) => item?.length > 0)) {
        active = true;
      }
      return active;
    };
    let newData = [];
    const originalData: CustomField[] = aircraftMap.get(id)?.custom_fields
      ? Object.values(aircraftMap.get(id).custom_fields)
      : [];

    if (originalData.length > 0 && checkIfFiltersActive()) {
      // pull out the active filter types from the filter object
      const filterKeys = Object.keys(filters);

      // filter data so that it returns data only that matches all active filters
      // but contains at least oneactive filter values
      const filterData = originalData.filter((customField) => {
        return filterKeys.every((key) => filters[key]?.includes(customField[key]?.toString()));
      });
      newData = filterData;
    } else {
      newData = originalData;
    }

    if (newData.length > 0 && searchValue) {
      newData = newData.filter((object) => {
        return Object.values(object).some((value) =>
          value?.toString()?.toLowerCase()?.includes(searchValue?.toLowerCase()),
        );
      });
    }

    if (newData.length > 0 && sortBy?.length > 0) {
      const iteratees = sortBy.map((sort) => (item) => {
        const value = item[sort.id];
        return typeof value === 'string' ? value.toLowerCase() : value;
      });
      const orders = sortBy.map((sort) => (sort.desc ? 'desc' : 'asc'));
      newData = _.orderBy(newData, iteratees, orders);
    }
    setCustomFieldsData([...newData]);
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aircraftMap, filters, id, searchValue, sortBy]);

  useEffect(() => {
    if (!_.isEqual(foundAircraftCustomFields, previousAircraftCustomFields)) {
      setLoading(false);
    }
  }, [foundAircraftCustomFields, previousAircraftCustomFields]);

  return (
    <InnerMenuLayoutNew>
      <TFLoading loading={loading} />
      <Wrapper data-testid="CustomFields--Wrapper">
        <GridWrapper>
          <TableColumn>
            <TableHeader data-testid="CustomFields--TableHeader">
              <Header data-testid="CustomFields--Header">
                {formatMessage({ id: 'text.customFields' })}
                <Count>{`(${customFieldsData?.length || '0'})`}</Count>
              </Header>
              <ActionWrapper>
                <SearchWrapper data-testid="CustomFields--SearchWrapper">
                  <Search
                    onChange={(e): void => setSearchValue(e.target.value)}
                    onClear={(): void => setSearchValue('')}
                  />
                </SearchWrapper>
                <AircraftAuthenticationWrapper
                  aircraftId={id}
                  requiredResource={AircraftResource.AIRCRAFT}
                  requiredPermissionLevel={AircraftPermission.CREATE}
                >
                  <TFDropdownMenu
                    buttonText={
                      <AddButtonText data-testid="CustomFields--AddButtonText">
                        <img src={plusIcon} alt="plus icon" />
                        {window.innerWidth < 451 ? formatMessage({ id: 'text.add' }) : 'Add new field'}
                      </AddButtonText>
                    }
                    options={addOptions()}
                    buttonPrimary
                  />
                </AircraftAuthenticationWrapper>
              </ActionWrapper>
            </TableHeader>
            <CustomFieldsTable
              data={customFieldsData || []}
              total={customFieldsData?.length}
              pageSize={pageSize}
              pageIndex={pageIndex - 1}
              setLoading={setLoading}
              requiredSettings={{ mxCheckRename, id }}
              onPaginationChange={(currentPage, numberOfItems): void => {
                if (numberOfItems !== pageSize) {
                  setPageIndex(1);
                  setPageSize(numberOfItems);
                } else if (currentPage !== pageIndex) {
                  setPageIndex(currentPage);
                }
              }}
              onSortChange={(sort): void => setSortBy(sort)}
            />
          </TableColumn>
          <FilterWrapper>
            <FilterSideBar
              data={aircraftMap.get(id)?.custom_fields ? Object.values(aircraftMap.get(id).custom_fields) : []}
              filterGroups={customFieldFilterModel({ mxCheckRename })}
              reset={reset}
              onChange={(e): void => {
                if (e.length === 0) {
                  setFilters({ required: [], type: [], view: [] });
                  setReset(false);
                } else {
                  const newFilters = e.reduce((newObject, object) => {
                    const workingObject = newObject;
                    if (!workingObject[object.key]) {
                      workingObject[object.key] = [object.value];
                    } else {
                      workingObject[object.key].push(object.value);
                    }
                    return workingObject;
                  }, {});
                  setFilters(newFilters);
                  setReset(false);
                }
              }}
            />
          </FilterWrapper>
        </GridWrapper>
      </Wrapper>
    </InnerMenuLayoutNew>
  );
};

export default CustomFields;
